import React, { useState } from 'react';
import {
  Badge,
  Box,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Popover,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import InboxIcon from '@mui/icons-material/Inbox';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
  NotificationItem,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../api/tasks';
import { useError } from '../../contexts/ErrorContext';

const parseSafeDate = (val: any): Date | null => {
  if (!val) return null;
  if (Array.isArray(val)) {
    const year = val[0] || 0;
    const month = (val[1] || 1) - 1;
    const day = val[2] || 1;
    const hour = val[3] || 0;
    const minute = val[4] || 0;
    const second = val[5] || 0;
    return new Date(year, month, day, hour, minute, second);
  }
  if (val instanceof Date) {
    return val;
  }
  const parsed = new Date(val);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const NotificationBell: React.FC = () => {
  const { user, isTeacher, isStudent } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useError();
  const userId = user?.id;
  const canSeeNotifications = isTeacher() || isStudent();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [justClosed, setJustClosed] = useState(false);
  const popoverOpen = Boolean(anchorEl);

  const { data: countData } = useQuery({
    queryKey: ['unread-notifications', userId],
    queryFn: async () => {
      const response = await getUnreadCount(userId!);
      return response.data;
    },
    refetchInterval: 60000,
    enabled: !!userId && canSeeNotifications,
  });

  const { data: notificationsData, refetch } = useQuery({
    queryKey: ['notifications', userId],
    queryFn: async () => {
      const response = await getNotifications(userId!, 0, 10);
      return response.data;
    },
    enabled: !!userId && popoverOpen && canSeeNotifications,
  });

  const markReadMutation = useMutation({
    mutationFn: (notificationId: number) => markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['unread-notifications', userId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => markAllNotificationsRead(userId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['unread-notifications', userId] });
      await queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
      await refetch();
      queryClient.invalidateQueries({ queryKey: ['tasks', 'teacher'] });
    },
    onError: () => showError('Failed to mark all notifications as read'),
  });

  if (!userId || !canSeeNotifications) {
    return null;
  }

  const unreadCount = countData?.count ?? 0;
  const badgeContent = unreadCount > 9 ? '9+' : unreadCount > 0 ? unreadCount : undefined;
  
  const isNotificationRead = (notification: NotificationItem) =>
    notification.isRead ?? (notification as any).read ?? false;

  const notifications = (notificationsData?.content ?? []).filter((n) => !isNotificationRead(n));

  const getSortTime = (val: any): number => {
    const d = parseSafeDate(val);
    return d ? d.getTime() : 0;
  };

  const sortedNotifications = [...notifications].sort((a, b) => getSortTime(b.createdAt) - getSortTime(a.createdAt));

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (justClosed) {
      setJustClosed(false);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') => {
    setAnchorEl(null);
    if (reason === 'backdropClick') {
      setJustClosed(true);
      setTimeout(() => {
        setJustClosed(false);
      }, 200);
    }
  };

  const navigateToTask = (notification: NotificationItem) => {
    navigate('/teacher/my-tasks', { state: { taskId: notification.taskId } });
  };

  const navigateToHomework = (notification: NotificationItem) => {
    // "taskId" is the generic field name reused by the backend to carry the homework id here.
    navigate('/student/homework', { state: { homeworkId: notification.taskId } });
  };

  const handleNotificationClick = async (notification: any) => {
    try {
      if (!isNotificationRead(notification)) {
        await markReadMutation.mutateAsync(notification.id);
      }

      queryClient.invalidateQueries({ queryKey: ['unread-notifications', userId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', userId] });
      queryClient.invalidateQueries({ queryKey: ['tasks', 'teacher'] });
      queryClient.invalidateQueries({ queryKey: ['homework', 'my'] });

      if (notification.notificationType === 'TASK_ASSIGNED') {
        navigateToTask(notification);
      } else if (notification.notificationType === 'ATTENDANCE_REQUEST_UPDATE') {
        navigate('/teacher/attendance-management');
      } else if (notification.notificationType === 'HOMEWORK_ASSIGNED') {
        navigateToHomework(notification);
      }

      handleClose();
    } catch {
      showError('Failed to update notification');
    }
  };

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ ml: 1, color: '#3092e3' }}>
        <Badge badgeContent={badgeContent} color="error">
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxWidth: '95vw' } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Notifications
          </Typography>
          <Link
            component="button"
            variant="body2"
            underline="hover"
            onClick={() => markAllReadMutation.mutate()}
            disabled={markAllReadMutation.isLoading || unreadCount === 0}
            sx={{ cursor: 'pointer' }}
          >
            {markAllReadMutation.isLoading ? 'Marking...' : 'Mark all as read'}
          </Link>
        </Box>
        <Divider />

        {sortedNotifications.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <InboxIcon sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant="body2">No new notifications</Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {sortedNotifications.map((notification) => (
              <ListItem
                key={notification.id}
                button
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  bgcolor: isNotificationRead(notification) ? 'transparent' : '#EEF4FF',
                  alignItems: 'flex-start',
                  py: 1.2,
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, mt: 0.5, color: '#3092e3' }}>
                  <NotificationsNoneIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={notification.message}
                  secondary={
                    (() => {
                      const d = parseSafeDate(notification.createdAt);
                      return d ? formatDistanceToNow(d, { addSuffix: true }) : 'just now';
                    })()
                  }
                  primaryTypographyProps={{ variant: 'body2' }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
